import React, { useCallback, useEffect, useRef, useState } from 'react';
import { send, waitForReady } from '../../ws';
import { waitForAck } from '../../utils/waitForAck';
import { correlationId } from '@interlucent/ids';
import { loadImageFromFile, cropToDataUri } from '../../utils/imageUtils';
import { useFocusTrap } from '../../utils/focusTrap';
import type { Application } from '@interlucent/organization-protocol';
import styles from './ChangeThumbnailDialog.module.css';

interface ChangeThumbnailDialogProps {
  isOpen: boolean;
  application: Application;
  onClose: () => void;
}

const VIEWPORT_W = 480;
const VIEWPORT_H = 270;

export const ChangeThumbnailDialog: React.FC<ChangeThumbnailDialogProps> = ({
  isOpen,
  application,
  onClose,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);

  // Phase: 'select' or 'crop'
  const [phase, setPhase] = useState<'select' | 'crop'>('select');

  // Loaded image for cropping
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  // Crop state
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [sliderMin, setSliderMin] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);

  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  // Saving state
  const [isSaving, setIsSaving] = useState(false);

  // Enable focus trap
  useFocusTrap(modalRef, isOpen);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setPhase('select');
      setImage(null);
      setScale(1);
      setMinScale(1);
      setSliderMin(1);
      setOffsetX(0);
      setOffsetY(0);
      setIsDragging(false);
      setIsSaving(false);
    }
  }, [isOpen]);

  // Store onClose in a ref to avoid re-running effect when callback changes
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Calculate initial crop parameters when image loads
  const initCrop = useCallback((img: HTMLImageElement) => {
    const scaleX = VIEWPORT_W / img.naturalWidth;
    const scaleY = VIEWPORT_H / img.naturalHeight;
    const coverScale = Math.max(scaleX, scaleY);
    const containScale = Math.min(scaleX, scaleY);

    setMinScale(coverScale);
    setSliderMin(containScale);
    setScale(coverScale);

    // Center the image within the viewport
    const displayW = img.naturalWidth * coverScale;
    const displayH = img.naturalHeight * coverScale;
    setOffsetX((VIEWPORT_W - displayW) / 2);
    setOffsetY((VIEWPORT_H - displayH) / 2);
  }, []);

  // Handle file selection
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const img = await loadImageFromFile(file);
      setImage(img);
      initCrop(img);
      setPhase('crop');
    } catch {
      // Failed to load image
    }

    // Reset file input so the same file can be selected again
    e.target.value = '';
  }, [initCrop]);

  // Handle paste
  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        if (!blob) continue;

        try {
          const img = await loadImageFromFile(blob);
          setImage(img);
          initCrop(img);
          setPhase('crop');
        } catch {
          // Failed to load pasted image
        }
        return;
      }
    }
  }, [initCrop]);

  // Clamp offsets to keep the image within reasonable bounds
  const clampOffset = useCallback((ox: number, oy: number, s: number, img: HTMLImageElement) => {
    const displayW = img.naturalWidth * s;
    const displayH = img.naturalHeight * s;

    let clampedX: number;
    let clampedY: number;

    if (displayW >= VIEWPORT_W) {
      // Image wider than viewport: keep edges covering viewport
      clampedX = Math.min(0, Math.max(VIEWPORT_W - displayW, ox));
    } else {
      // Image narrower than viewport: keep within viewport bounds
      clampedX = Math.max(0, Math.min(VIEWPORT_W - displayW, ox));
    }

    if (displayH >= VIEWPORT_H) {
      clampedY = Math.min(0, Math.max(VIEWPORT_H - displayH, oy));
    } else {
      clampedY = Math.max(0, Math.min(VIEWPORT_H - displayH, oy));
    }

    return { x: clampedX, y: clampedY };
  }, []);

  // Handle zoom slider change
  const handleZoomChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!image) return;
    const newScale = parseFloat(e.target.value);

    // When zooming, keep the center of the viewport fixed
    const centerX = VIEWPORT_W / 2;
    const centerY = VIEWPORT_H / 2;

    // Point in the image that's currently at the center
    const imgCenterX = (centerX - offsetX) / scale;
    const imgCenterY = (centerY - offsetY) / scale;

    // New offset to keep that same point at the center
    const newOffsetX = centerX - imgCenterX * newScale;
    const newOffsetY = centerY - imgCenterY * newScale;

    const clamped = clampOffset(newOffsetX, newOffsetY, newScale, image);
    setScale(newScale);
    setOffsetX(clamped.x);
    setOffsetY(clamped.y);
  }, [image, offsetX, offsetY, scale, clampOffset]);

  // Pointer drag handlers
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!image) return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY, offsetX, offsetY };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [image, offsetX, offsetY]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging || !image) return;

    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;

    const newOffsetX = dragStartRef.current.offsetX + dx;
    const newOffsetY = dragStartRef.current.offsetY + dy;

    const clamped = clampOffset(newOffsetX, newOffsetY, scale, image);
    setOffsetX(clamped.x);
    setOffsetY(clamped.y);
  }, [isDragging, image, scale, clampOffset]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Remove thumbnail
  const handleRemoveThumbnail = useCallback(async () => {
    await waitForReady();
    const corr = correlationId();
    send('application.update', {
      id: application.id,
      thumbnailDataUri: null,
      rowVersion: application.rowVersion ?? 0,
    }, corr);
    onClose();
  }, [application.id, application.rowVersion, onClose]);

  // Save cropped thumbnail
  const handleSave = useCallback(async () => {
    if (!image) return;

    setIsSaving(true);
    try {
      const dataUri = cropToDataUri(image, scale, offsetX, offsetY, VIEWPORT_W, VIEWPORT_H);
      await waitForReady();
      const corr = correlationId();
      send('application.update', {
        id: application.id,
        thumbnailDataUri: dataUri,
        rowVersion: application.rowVersion ?? 0,
      }, corr);
      await waitForAck(corr);
      onClose();
    } catch {
      // Failed to save
    } finally {
      setIsSaving(false);
    }
  }, [image, scale, offsetX, offsetY, application.id, application.rowVersion, onClose]);

  // Go back to selection phase
  const handleBack = useCallback(() => {
    setPhase('select');
    setImage(null);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className={styles.modalOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-thumbnail-title"
    >
      <div ref={modalRef} className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 id="change-thumbnail-title" className={styles.modalTitle}>Change Thumbnail</h2>

        {phase === 'select' && (
          <>
            {/* Current thumbnail preview */}
            <div className={styles.currentThumbnail}>
              {application.thumbnailDataUri ? (
                <img src={application.thumbnailDataUri} alt="Current thumbnail" />
              ) : (
                <div className={styles.placeholderIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                  No thumbnail
                </div>
              )}
            </div>

            {/* Selection actions */}
            <div className={styles.selectionActions}>
              <div className={styles.selectionButtons}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={`${styles.button} ${styles.buttonPrimary}`}
                >
                  Choose File
                </button>
                {application.thumbnailDataUri && (
                  <button
                    onClick={handleRemoveThumbnail}
                    className={`${styles.button} ${styles.buttonDanger}`}
                  >
                    Remove Thumbnail
                  </button>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className={styles.hiddenInput}
              />

              {/* Paste zone */}
              <div
                className={styles.pasteZone}
                onPaste={handlePaste}
                tabIndex={0}
              >
                Or paste an image (Ctrl+V / Cmd+V)
              </div>
            </div>

            {/* Cancel button */}
            <div className={styles.buttonGroup}>
              <button
                onClick={onClose}
                className={`${styles.button} ${styles.buttonSecondary}`}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {phase === 'crop' && image && (
          <>
            {/* Crop preview */}
            <div className={styles.cropContainer}>
              <div
                ref={previewRef}
                className={styles.cropPreview}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
              >
                <img
                  src={image.src}
                  alt="Crop preview"
                  style={{
                    transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
                  }}
                />
              </div>

              {/* Zoom slider */}
              <div className={styles.zoomControl}>
                <span className={styles.zoomLabel}>Zoom</span>
                <input
                  type="range"
                  className={styles.zoomSlider}
                  min={sliderMin}
                  max={minScale * 3}
                  step={0.001}
                  value={scale}
                  onChange={handleZoomChange}
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className={styles.buttonGroup}>
              <button
                onClick={handleBack}
                className={`${styles.button} ${styles.buttonSecondary}`}
              >
                Back
              </button>
              <button
                onClick={onClose}
                className={`${styles.button} ${styles.buttonSecondary}`}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`${styles.button} ${styles.buttonPrimary}`}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
